import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import Shell from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { LayoutDashboard, Store, Users, DollarSign, Settings, Loader2, TrendingUp, Trash2, StickyNote, Ban, Map as MapIcon, Layers, Milestone, Eye, ShieldCheck, Tags, QrCode, Download, CreditCard, Megaphone, Inbox, FlaskConical, Building2, Route, Bot, MapPinOff, GraduationCap, FolderOpen, MapPinned } from "lucide-react";
import { toast } from "sonner";
import { PARTNER_TYPE_LABEL, PARTNER_CATEGORY_LABEL, PARTNER_TYPES } from "@/lib/partnerTypes";
import BikePathsPanel from "@/components/admin/BikePathsPanel";
import RoutesPanel from "@/components/admin/RoutesPanel";
import ZonesPanel from "@/components/admin/ZonesPanel";
import ContentManager from "@/components/content/ContentManager";
import RiderPreview from "@/components/rider/RiderPreview";
import ModerationPanel from "@/components/admin/ModerationPanel";
import ManagedListingsPanel from "@/components/admin/ManagedListingsPanel";
import OutsideZonePanel from "@/components/admin/OutsideZonePanel";
import ModerationQueue from "@/components/admin/ModerationQueue";
import CategoryManager from "@/components/admin/CategoryManager";
import PreviewQRPanel from "@/components/admin/PreviewQRPanel";
import ShopReceiptsDialog from "@/components/admin/ShopReceiptsDialog";
import PaymentsHealthPanel from "@/components/admin/PaymentsHealthPanel";
import ZoneBillingPanel from "@/components/admin/ZoneBillingPanel";
import ZoneManagementPanel from "@/components/admin/ZoneManagementPanel";
import TopPerformingZones from "@/components/admin/TopPerformingZones";
import OrphanProspectsWidget from "@/components/admin/OrphanProspectsWidget";
import GoogleUsageWidget from "@/components/admin/GoogleUsageWidget";
import PlatformMetrics from "@/components/admin/PlatformMetrics";
import PromotionsPanel from "@/components/admin/PromotionsPanel";
import ZoneRequestsPanel from "@/components/admin/ZoneRequestsPanel";
import TrailLab from "@/components/admin/TrailLab";
import ProspectsPanel from "@/components/admin/ProspectsPanel";
import AcademyAdminPanel from "@/components/admin/AcademyAdminPanel";
import MarketingAdminPanel from "@/components/admin/MarketingAdminPanel";
import TrailheadsAdminPanel from "@/components/admin/TrailheadsAdminPanel";
import CoverageRequestsPanel from "@/components/admin/CoverageRequestsPanel";
import MetricsDashboard from "@/components/admin/MetricsDashboard";
import AdvertiserInvoicesDialog from "@/components/admin/AdvertiserInvoicesDialog";
import AssistantAdminPanel from "@/components/admin/assistant/AssistantAdminPanel";

function ReviewNotes({ shop, onSave }) {
  const [notes, setNotes] = useState(shop.admin_notes || "");
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="ghost" className={shop.admin_notes ? "text-amber-400" : "text-muted-foreground"} data-testid={`notes-shop-${shop.id}`}><StickyNote className="w-4 h-4" /></Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Private review notes — {shop.name}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">Only Velopaths admins can see these notes.</p>
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={5} placeholder="e.g. Verified business license, waiting on insurance doc…" data-testid={`notes-input-${shop.id}`} />
        <DialogFooter>
          <Button onClick={async () => { await onSave(notes); setOpen(false); }} data-testid={`save-notes-${shop.id}`}>Save notes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SetPendingDialog({ shop, onConfirm }) {
  const [reason, setReason] = useState("");
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`revoke-shop-${shop.id}`}><Ban className="w-4 h-4 mr-1" />Set pending</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Set "{shop.name}" back to pending</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Add an optional short reason. It will be emailed to the shop owner so they know what to fix.</p>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="e.g. Please add a valid business website and complete your payout details." data-testid={`reject-reason-${shop.id}`} />
        <DialogFooter>
          <DialogClose asChild><Button variant="ghost">Cancel</Button></DialogClose>
          <Button className="bg-amber-500 text-black hover:bg-amber-500/90" onClick={async () => { await onConfirm(reason); setOpen(false); }} data-testid={`confirm-reject-${shop.id}`}>Set pending & notify</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ icon: Icon, label, value, color = "text-primary" }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <Icon className={`w-5 h-5 ${color} mb-3`} />
      <div className="text-3xl font-heading font-extrabold">{value}</div>
      <div className="text-sm text-muted-foreground">{label}</div>
    </div>
  );
}

function PartnerColumn({ icon: Icon, type, total, color, counts }) {
  const cats = PARTNER_TYPES[type].categories;
  const other = Math.max(0, total - cats.reduce((s, c) => s + (counts[c.value] || 0), 0));
  return (
    <div className="rounded-2xl border border-border bg-card p-6" data-testid={`partner-col-${type}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><Icon className={`w-5 h-5 ${color}`} /><span className="font-heading font-bold">{PARTNER_TYPE_LABEL[type]}s</span></div>
        <span className={`text-2xl font-heading font-extrabold ${color}`} data-testid={`partner-total-${type}`}>{total}</span>
      </div>
      <div className="mt-4 space-y-2">
        {cats.map((c) => (
          <div key={c.value} className="flex items-center justify-between text-sm" data-testid={`partner-cat-${c.value}`}>
            <span className="text-muted-foreground">{c.label}</span>
            <span className="font-semibold tabular-nums">{counts[c.value] || 0}</span>
          </div>
        ))}
        {other > 0 && (
          <div className="flex items-center justify-between text-sm" data-testid={`partner-cat-${type}-other`}>
            <span className="text-muted-foreground">Unspecified</span>
            <span className="font-semibold tabular-nums">{other}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function PartnersOverview({ overview }) {
  const counts = {};
  (overview.partner_categories || []).forEach((r) => { if (r.category) counts[r.category] = (counts[r.category] || 0) + r.count; });
  return (
    <div data-testid="partner-type-breakdown">
      <h3 className="font-heading font-bold mb-3">Partners Overview</h3>
      <div className="grid sm:grid-cols-2 gap-4">
        <PartnerColumn icon={Store} type="business" total={overview.business_partners ?? 0} color="text-blue-400" counts={counts} />
        <PartnerColumn icon={Route} type="trail" total={overview.trail_partners ?? 0} color="text-emerald-400" counts={counts} />
      </div>
    </div>
  );
}


export default function AdminDashboard() {
  const [tab, setTab] = useState("overview");
  const [zonesPathId, setZonesPathId] = useState(null);
  const [qrShop, setQrShop] = useState(null);
  const [overview, setOverview] = useState(null);
  const [shops, setShops] = useState([]);
  const [shopFilter, setShopFilter] = useState("all");
  const [partnerTypeFilter, setPartnerTypeFilter] = useState("all");
  const [advertisers, setAdvertisers] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [share, setShare] = useState(70);
  const [fee, setFee] = useState(99);
  const [reminderDays, setReminderDays] = useState(30);
  const [deletedShops, setDeletedShops] = useState([]);
  const [savingShare, setSavingShare] = useState(false);
  const [advAnnual, setAdvAnnual] = useState(99);
  const [savingPricing, setSavingPricing] = useState(false);
  const [promoPrice, setPromoPrice] = useState("");
  const [promoStart, setPromoStart] = useState("");
  const [promoEnd, setPromoEnd] = useState("");
  const [promoStatus, setPromoStatus] = useState("none");
  const [savingPromo, setSavingPromo] = useState(false);

  const loadAll = async () => {
    const [o, sh, ad, po, se, del_, pr, pm] = await Promise.all([
      api.get("/admin/overview"),
      api.get("/admin/shops"),
      api.get("/admin/advertisers"),
      api.get("/admin/payouts"),
      api.get("/admin/settings"),
      api.get("/admin/shops/deleted"),
      api.get("/admin/advertiser-pricing"),
      api.get("/admin/advertiser-promo"),
    ]);
    setOverview(o.data); setShops(sh.data); setAdvertisers(ad.data); setPayouts(po.data);
    setShare(se.data.revenue_share_percent); setFee(se.data.setup_fee ?? 99); setReminderDays(se.data.renewal_reminder_days ?? 30); setDeletedShops(del_.data);
    setAdvAnnual(pr.data.annual);
    setPromoPrice(pm.data.price ?? ""); setPromoStart(pm.data.start ?? ""); setPromoEnd(pm.data.end ?? ""); setPromoStatus(pm.data.status);
  };

  useEffect(() => { loadAll(); }, []);

  const [pendingModeration, setPendingModeration] = useState(0);
  useEffect(() => { api.get("/admin/moderation/queue").then((r) => setPendingModeration(r.data?.counts?.pending || 0)).catch(() => {}); }, []);
  const [pendingManaged, setPendingManaged] = useState(0);
  const [pendingPins, setPendingPins] = useState(0);
  useEffect(() => { api.get("/admin/amenities/pending").then((r) => setPendingPins((r.data || []).length)).catch(() => {}); }, []);
  useEffect(() => { api.get("/admin/managed-listings").then((r) => setPendingManaged(r.data?.counts?.pending || 0)).catch(() => {}); }, []);

  const saveShare = async () => {
    setSavingShare(true);
    try { await api.put("/admin/settings", { revenue_share_percent: share, renewal_reminder_days: Number(reminderDays) }); toast.success("Settings updated"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSavingShare(false); }
  };

  const savePricing = async () => {
    setSavingPricing(true);
    try {
      const { data } = await api.put("/admin/advertiser-pricing", { annual: Number(advAnnual) });
      setAdvAnnual(data.annual);
      toast.success("Advertiser pricing updated in Stripe");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSavingPricing(false); }
  };

  const savePromo = async () => {
    setSavingPromo(true);
    try {
      const { data } = await api.put("/admin/advertiser-promo", { price: Number(promoPrice), start: promoStart || null, end: promoEnd || null });
      setPromoPrice(data.price ?? ""); setPromoStart(data.start ?? ""); setPromoEnd(data.end ?? ""); setPromoStatus(data.status);
      toast.success("Promo saved");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSavingPromo(false); }
  };

  const endPromo = async () => {
    setSavingPromo(true);
    try {
      const { data } = await api.post("/admin/advertiser-promo/end");
      setPromoPrice(""); setPromoStart(""); setPromoEnd(""); setPromoStatus(data.status);
      toast.success("Promo ended");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSavingPromo(false); }
  };

  const restoreShop = async (id) => { await api.post(`/admin/shops/${id}/restore`); await loadAll(); toast.success("Shop restored"); };
  const purgeShop = async (id) => { await api.delete(`/admin/shops/${id}/purge`); await loadAll(); toast.success("Shop permanently deleted"); };
  const deleteAdvertiser = async (id) => { await api.delete(`/admin/advertisers/${id}`); await loadAll(); toast.success("Advertiser deleted"); };

  const markPaid = async (id) => { await api.post(`/admin/payouts/${id}/mark-paid`); loadAll(); toast.success("Payout marked paid"); };

  const markShopPaid = async (id) => { await api.post(`/admin/shops/${id}/mark-paid`); await loadAll(); toast.success("Shop marked paid & activated"); };

  const uncompShop = async (id) => { await api.post(`/admin/shops/${id}/uncomp`); await loadAll(); toast.success("Comp reversed — shop set to approved · unpaid"); };

  const setShopApproval = async (id, approve) => {
    await api.post(`/admin/shops/${id}/approve`);
    await loadAll();
    toast.success("Shop approved — now live for advertisers");
  };

  const setShopPending = async (id, reason) => {
    await api.post(`/admin/shops/${id}/reject`, { reason: reason || "" });
    await loadAll();
    toast.success("Shop set to pending — owner notified");
  };

  const resendPending = async (id) => {
    try {
      const { data } = await api.post(`/admin/shops/${id}/resend-pending-email`);
      toast.success(`Pending-review email resent to ${data.sent_to}`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const resendApproval = async (id) => {
    try {
      const { data } = await api.post(`/admin/shops/${id}/resend-approval-email`);
      toast.success(`Approval + payment email resent to ${data.sent_to}`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const shopState = (s) => (s.active ? "active" : s.approved ? "approved" : (s.approved_at ? "reverted" : "pending"));

  const saveNotes = async (id, notes) => {
    await api.put(`/admin/shops/${id}/notes`, { notes });
    await loadAll();
    toast.success("Notes saved");
  };

  const pendingShops = shops.filter((s) => s.approved === false).length;

  const deleteShop = async (id) => {
    await api.delete(`/admin/shops/${id}`);
    await loadAll();
    toast.success("Shop and all its data deleted");
  };

  const nav = [
    { key: "overview", label: "Overview", icon: LayoutDashboard },
    { key: "metrics", label: "Business Dashboard", icon: TrendingUp },
    { key: "assistant", label: "AI Assistant", icon: Bot },
    { key: "bikepaths", label: "Bike Paths", icon: MapIcon },
    { key: "routes", label: "Routes", icon: Route },
    { key: "trailheads", label: "Trailheads & Amenities", icon: MapPinned, count: pendingPins },
    { key: "zones", label: "Zones", icon: Layers },
    { key: "zonemgmt", label: "Zone Management", icon: Layers },
    { key: "zonerequests", label: "Zone Requests", icon: Inbox },
    { key: "traillab", label: "Trail Lab", icon: FlaskConical },
    { key: "zonebilling", label: "Zone Billing", icon: CreditCard },
    { key: "promotions", label: "Promotions", icon: Megaphone },
    { key: "globalcontent", label: "Global Content", icon: Milestone },
    { key: "riderpreview", label: "Rider Preview", icon: Eye },
    { key: "previewqr", label: "Preview QR Codes", icon: QrCode },
    { key: "reviewqueue", label: "Review Queue", icon: Inbox, count: pendingModeration },
    { key: "moderation", label: "Content Admin", icon: ShieldCheck },
    { key: "categories", label: "Content Categories", icon: Tags },
    { key: "shops", label: "Shops", icon: Store, count: pendingShops },
    { key: "prospects", label: "Prospects", icon: Building2 },
    { key: "advertisers", label: "Advertisers", icon: Users },
    { key: "managedlistings", label: "Managed Listings", icon: Inbox, count: pendingManaged },
    { key: "outsidezone", label: "Outside-Zone Report", icon: MapPinOff },
    { key: "coverage", label: "Coverage Requests", icon: Inbox },
    { key: "payouts", label: "Payout Logs", icon: DollarSign },
    { key: "payments", label: "Payments Health", icon: CreditCard },
    { key: "academy", label: "Partner Academy", icon: GraduationCap },
    { key: "marketing", label: "Marketing Assets", icon: FolderOpen },
    { key: "settings", label: "Settings", icon: Settings },
  ];

  if (!overview) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <Shell nav={nav} active={tab} onNavigate={setTab} title="Velopaths Admin" badge="Global Admin" fluid={tab === "prospects" || tab === "zonemgmt"}>
      {tab === "overview" && (
        <div className="space-y-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat icon={Store} label="Partners" value={overview.shops} />
            <Stat icon={Users} label="Advertisers" value={overview.advertisers} color="text-orange-400" />
            <Stat icon={LayoutDashboard} label="Listings" value={overview.listings} color="text-cyan-400" />
            <Stat icon={TrendingUp} label="Attractions" value={overview.attractions} color="text-amber-400" />
          </div>
          <PartnersOverview overview={overview} />
          <OrphanProspectsWidget onOpenPath={(id) => { setZonesPathId(id); setTab("zones"); }} />
          <GoogleUsageWidget />
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="rounded-2xl border border-border bg-card p-6"><div className="text-sm text-muted-foreground">Gross revenue</div><div className="text-3xl font-heading font-extrabold mt-1" data-testid="admin-gross-revenue">${overview.gross_revenue.toFixed(2)}</div></div>
            <div className="rounded-2xl border border-border bg-card p-6"><div className="text-sm text-muted-foreground">Shop payouts</div><div className="text-3xl font-heading font-extrabold mt-1 text-amber-400">${overview.payout_total.toFixed(2)}</div></div>
            <div className="rounded-2xl border border-primary/40 bg-primary/5 p-6"><div className="text-sm text-muted-foreground">Velopaths revenue</div><div className="text-3xl font-heading font-extrabold mt-1 text-primary">${overview.velopaths_revenue.toFixed(2)}</div></div>
          </div>
          <PlatformMetrics />
          <TopPerformingZones />
        </div>
      )}

      {tab === "bikepaths" && <BikePathsPanel />}
      {tab === "routes" && <RoutesPanel />}
      {tab === "trailheads" && <TrailheadsAdminPanel onPendingCount={setPendingPins} />}

      {tab === "zones" && <ZonesPanel initialPathId={zonesPathId} />}

      {tab === "zonebilling" && <ZoneBillingPanel />}

      {tab === "promotions" && <PromotionsPanel />}

      {tab === "zonemgmt" && <ZoneManagementPanel />}

      {tab === "zonerequests" && <ZoneRequestsPanel />}
      {tab === "traillab" && <TrailLab />}
      {tab === "prospects" && <ProspectsPanel />}
      {tab === "academy" && <AcademyAdminPanel />}
      {tab === "marketing" && <MarketingAdminPanel />}
      {tab === "metrics" && <MetricsDashboard />}
      {tab === "assistant" && <AssistantAdminPanel />}

      {tab === "globalcontent" && <ContentManager mode="global" />}

      {tab === "riderpreview" && <RiderPreview />}

      {tab === "previewqr" && <PreviewQRPanel initialShopId={qrShop} />}

      {tab === "moderation" && <ModerationPanel />}
      {tab === "reviewqueue" && <ModerationQueue onCountChange={setPendingModeration} />}
      {tab === "coverage" && <CoverageRequestsPanel />}
      {tab === "managedlistings" && <ManagedListingsPanel onCountChange={setPendingManaged} />}
      {tab === "outsidezone" && <OutsideZonePanel />}

      {tab === "categories" && <CategoryManager />}

      {tab === "shops" && (
        <div className="rounded-2xl border border-border bg-card overflow-hidden">
          <div className="flex flex-wrap gap-2 p-4 border-b border-border" data-testid="shop-filter">
            {[["all", "All"], ["pending", "Pending"], ["reverted", "Reverted"], ["approved", "Approved · unpaid"], ["active", "Active"], ["kyc_pending", "KYC pending"]].map(([v, l]) => (
              <Button key={v} size="sm" variant={shopFilter === v ? "default" : "outline"} onClick={() => setShopFilter(v)} data-testid={`shop-filter-${v}`}>{l}</Button>
            ))}
            <span className="w-px bg-border mx-1 self-stretch" />
            {[["all", "All types"], ["business", "Business"], ["trail", "Trail"]].map(([v, l]) => (
              <Button key={v} size="sm" variant={partnerTypeFilter === v ? "default" : "outline"} onClick={() => setPartnerTypeFilter(v)} data-testid={`shop-type-filter-${v}`}>{l}</Button>
            ))}
            <Button size="sm" variant="outline" className="ml-auto" data-testid="receipts-export-all" onClick={async () => { try { const { data } = await api.get("/admin/receipts/export", { responseType: "blob" }); const href = URL.createObjectURL(data); const a = document.createElement("a"); a.href = href; a.download = "velopaths-receipts.csv"; a.click(); URL.revokeObjectURL(href); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } }}><Download className="w-4 h-4 mr-1" />Export receipts CSV</Button>
          </div>
          <Table>
            <TableHeader><TableRow><TableHead>Partner</TableHead><TableHead>Type</TableHead><TableHead>Region</TableHead><TableHead>Website</TableHead><TableHead>Status</TableHead><TableHead>Payouts</TableHead><TableHead>Advertisers</TableHead><TableHead>Attractions</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {shops.filter((s) => (shopFilter === "all" || (shopFilter === "kyc_pending" ? s.payout_status === "pending" : shopState(s) === shopFilter)) && (partnerTypeFilter === "all" || (s.partner_type || "business") === partnerTypeFilter)).map((s) => (
                <TableRow key={s.id} data-testid={`admin-shop-row-${s.id}`}>
                  <TableCell className="font-medium">{s.name}</TableCell>
                  <TableCell className="text-xs" data-testid={`shop-type-${s.id}`}>{s.partner_category ? (PARTNER_CATEGORY_LABEL[s.partner_category] || s.partner_category) : "—"}{s.partner_type ? <span className="block text-[10px] text-muted-foreground">{PARTNER_TYPE_LABEL[s.partner_type] || s.partner_type}</span> : null}</TableCell>
                  <TableCell>{s.region}</TableCell>
                  <TableCell className="max-w-[180px] truncate">{s.website ? <a href={s.website} target="_blank" rel="noreferrer" className="text-primary hover:underline">{s.website}</a> : <span className="text-muted-foreground">—</span>}</TableCell>
                  <TableCell>
                    <Badge className={s.active ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30" : s.approved ? "bg-cyan-500/15 text-cyan-400 border border-cyan-500/30" : "bg-amber-500/15 text-amber-400 border border-amber-500/30"} data-testid={`shop-status-${s.id}`}>
                      {s.active ? (s.comped ? "active · comped" : "active") : s.approved ? "approved · unpaid" : "pending"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={s.payout_status === "enabled" ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30" : s.payout_status === "pending" ? "bg-amber-500/15 text-amber-400 border border-amber-500/30" : "bg-muted text-muted-foreground border border-border"} data-testid={`shop-payout-status-${s.id}`}>
                      {s.payout_status === "enabled" ? "KYC complete" : s.payout_status === "pending" ? "KYC pending" : "Not started"}
                    </Badge>
                  </TableCell>
                  <TableCell>{s.advertisers}</TableCell>
                  <TableCell>{s.attractions}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {s.approved ? (
                        <>
                          <SetPendingDialog shop={s} onConfirm={(reason) => setShopPending(s.id, reason)} />
                          <Button size="sm" variant="outline" onClick={() => resendApproval(s.id)} data-testid={`resend-approval-${s.id}`}>Resend approval</Button>
                        </>
                      ) : (
                        <>
                          <Button size="sm" onClick={() => setShopApproval(s.id, true)} data-testid={`approve-shop-${s.id}`}>Approve</Button>
                          <Button size="sm" variant="outline" onClick={() => resendPending(s.id)} data-testid={`resend-pending-${s.id}`}>Resend email</Button>
                        </>
                      )}
                      <Button size="sm" variant="ghost" title="Rider Preview QR code" onClick={() => { setQrShop(s.id); setTab("previewqr"); }} data-testid={`shop-qr-${s.id}`}><QrCode className="w-4 h-4" /></Button>
                      {!s.active && <Button size="sm" variant="outline" onClick={() => markShopPaid(s.id)} data-testid={`mark-shop-paid-${s.id}`}>Mark paid</Button>}
                      {s.comped && <Button size="sm" variant="outline" className="text-amber-600 border-amber-500/40 hover:text-amber-600" onClick={() => uncompShop(s.id)} data-testid={`uncomp-shop-${s.id}`}>Un-comp</Button>}
                      {s.active && <ShopReceiptsDialog shopId={s.id} shopName={s.name} />}
                      <ReviewNotes shop={s} onSave={(notes) => saveNotes(s.id, notes)} />
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" data-testid={`delete-shop-${s.id}`}><Trash2 className="w-4 h-4" /></Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete "{s.name}"?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This permanently removes the shop and all of its data — advertisers, listings, categories, attractions, subscriptions and payout records. The shop is recoverable for 30 days from the Settings tab before it is purged for good.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => deleteShop(s.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid={`confirm-delete-shop-${s.id}`}>Delete shop</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {tab === "advertisers" && (
        <div className="rounded-2xl border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader><TableRow><TableHead>Business</TableHead><TableHead>Shop</TableHead><TableHead>Subscription</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {advertisers.map((a) => (
                <TableRow key={a.id}>
                  <TableCell className="font-medium">{a.business_name}</TableCell>
                  <TableCell>{a.shop_name}</TableCell>
                  <TableCell><Badge className={a.subscription_status === "active" ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30" : "bg-slate-500/15 text-slate-300 border border-slate-500/30"}>{a.subscription_status}</Badge></TableCell>
                  <TableCell>
                    <AdvertiserInvoicesDialog advertiserId={a.id} name={a.business_name} />
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" data-testid={`delete-advertiser-${a.id}`}><Trash2 className="w-4 h-4" /></Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete "{a.business_name}"?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This permanently removes the advertiser and all of its data — listing, subscription and payment records. This cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => deleteAdvertiser(a.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid={`confirm-delete-advertiser-${a.id}`}>Delete advertiser</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {tab === "payouts" && (
        <div className="rounded-2xl border border-border bg-card overflow-hidden">
          <Table data-testid="admin-payout-logs">
            <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Shop</TableHead><TableHead>Gross</TableHead><TableHead>Share</TableHead><TableHead>Payout</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {payouts.length === 0 && <TableRow><TableCell colSpan={7} className="text-muted-foreground">No payouts recorded yet.</TableCell></TableRow>}
              {payouts.map((p) => (
                <TableRow key={p.id}>
                  <TableCell>{new Date(p.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>{p.shop_name}</TableCell>
                  <TableCell>${p.gross_amount?.toFixed(2)}</TableCell>
                  <TableCell>{p.revenue_share_percent}%</TableCell>
                  <TableCell className="font-bold text-primary">${p.amount?.toFixed(2)}</TableCell>
                  <TableCell><Badge className={p.status === "paid" ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30" : "bg-amber-500/15 text-amber-400 border border-amber-500/30"}>{p.status}</Badge></TableCell>
                  <TableCell>{p.status !== "paid" && <Button size="sm" variant="outline" onClick={() => markPaid(p.id)} data-testid={`mark-paid-${p.id}`}>Mark paid</Button>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {tab === "payments" && <PaymentsHealthPanel />}

      {tab === "settings" && (
        <div className="max-w-lg rounded-2xl border border-border bg-card p-6 space-y-6">
          <div>
            <h3 className="font-heading text-xl font-bold">Global revenue share</h3>
            <p className="text-sm text-muted-foreground mt-1">The percentage of each advertiser payment that goes to the bike rental shop.</p>
          </div>
          <div>
            <div className="flex items-end justify-between mb-3">
              <span className="text-sm text-muted-foreground">Shop share</span>
              <span className="text-4xl font-heading font-extrabold text-primary" data-testid="revenue-share-value">{share}%</span>
            </div>
            <Slider value={[share]} onValueChange={(v) => setShare(v[0])} min={0} max={100} step={5} data-testid="revenue-share-slider" />
            <div className="text-xs text-muted-foreground mt-2">Velopaths keeps {100 - share}%</div>
          </div>
          <div>
            <div className="text-sm text-muted-foreground mb-2">Founding Partner setup fee</div>
            <div className="rounded-lg border border-primary/20 bg-primary/10 px-3 py-2.5 text-sm" data-testid="setup-fee-display">
              <span className="font-heading font-bold text-base">${Number(fee) % 1 === 0 ? fee : Number(fee).toFixed(2)}</span> one-time
              <span className="text-muted-foreground"> — includes first zone + one year of participation</span>
            </div>
            <div className="text-xs text-muted-foreground mt-1">Set by the live Stripe <code>shop_setup_fee</code> price. Additional zones are leased separately per territory.</div>
          </div>
          <div>
            <div className="text-sm text-muted-foreground mb-2">Renewal reminder lead time</div>
            <div className="flex gap-2" data-testid="reminder-days-options">
              {[30, 14, 7].map((d) => (
                <Button key={d} type="button" variant={Number(reminderDays) === d ? "default" : "outline"} onClick={() => setReminderDays(d)} data-testid={`reminder-days-${d}`}>{d} days</Button>
              ))}
            </div>
            <div className="text-xs text-muted-foreground mt-1">How many days before an advertiser's annual renewal the reminder email is sent.</div>
          </div>
          <Button onClick={saveShare} disabled={savingShare} data-testid="save-revenue-share-button">{savingShare && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Save settings</Button>
        </div>
      )}

      {tab === "settings" && (
        <div className="max-w-lg mt-6 rounded-2xl border border-border bg-card p-6 space-y-5" data-testid="advertiser-pricing-card">
          <div>
            <h3 className="font-heading text-xl font-bold">Founding Advertiser pricing</h3>
            <p className="text-sm text-muted-foreground mt-1">What advertisers pay per location to be featured. Saving updates the live price in Stripe. Existing subscribers keep their current price until their next renewal; new and renewing advertisers get the new price.</p>
          </div>
          <div className="grid grid-cols-1 gap-3">
            <div>
              <div className="text-sm text-muted-foreground mb-2">Annual price ($/year per location)</div>
              <input type="number" min="1" step="1" value={advAnnual} onChange={(e) => setAdvAnnual(e.target.value)} data-testid="advertiser-annual-price-input" className="w-full bg-secondary border border-border rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>
          <div className="text-xs text-muted-foreground" data-testid="advertiser-pricing-summary">
            Founding Advertisers will pay ${Number(advAnnual) % 1 === 0 ? advAnnual : Number(advAnnual).toFixed(2)}/year per location.
          </div>
          <Button onClick={savePricing} disabled={savingPricing} data-testid="save-advertiser-pricing-button">{savingPricing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Update advertiser pricing</Button>
        </div>
      )}

      {tab === "settings" && (
        <div className="max-w-lg mt-6 rounded-2xl border border-border bg-card p-6 space-y-5" data-testid="advertiser-promo-card">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="font-heading text-xl font-bold">Limited-time signup promo (Annual)</h3>
              <p className="text-sm text-muted-foreground mt-1">A discounted first-year price to drive new advertiser signups. Brand-new advertisers who subscribe inside the window pay the promo price for their first year, then renew at the regular annual price. Existing advertisers are unaffected.</p>
            </div>
            <Badge
              data-testid="promo-status-badge"
              className={
                promoStatus === "active" ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                : promoStatus === "scheduled" ? "bg-sky-500/15 text-sky-300 border border-sky-500/30"
                : promoStatus === "ended" ? "bg-zinc-500/15 text-zinc-300 border border-zinc-500/30"
                : "bg-zinc-500/10 text-muted-foreground border border-border"
              }
            >
              {promoStatus === "active" ? "Live now" : promoStatus === "scheduled" ? "Scheduled" : promoStatus === "ended" ? "Ended" : "No promo"}
            </Badge>
          </div>
          <div>
            <div className="text-sm text-muted-foreground mb-2">Promo first-year price ($)</div>
            <input type="number" min="1" step="1" value={promoPrice} onChange={(e) => setPromoPrice(e.target.value)} placeholder={`e.g. ${Math.max(1, Math.round(Number(advAnnual) * 0.75))}`} data-testid="promo-price-input" className="w-full bg-secondary border border-border rounded-lg px-3 py-2 text-sm" />
            <div className="text-xs text-muted-foreground mt-1">Must be below the regular ${Number(advAnnual) % 1 === 0 ? advAnnual : Number(advAnnual).toFixed(2)}/year price.</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-sm text-muted-foreground mb-2">Start date</div>
              <input type="date" value={promoStart || ""} onChange={(e) => setPromoStart(e.target.value)} data-testid="promo-start-input" className="w-full bg-secondary border border-border rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <div className="text-sm text-muted-foreground mb-2">Auto-expiry date</div>
              <input type="date" value={promoEnd || ""} onChange={(e) => setPromoEnd(e.target.value)} data-testid="promo-end-input" className="w-full bg-secondary border border-border rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>
          {Number(promoPrice) > 0 && (
            <div className="text-xs text-emerald-400" data-testid="promo-summary">
              New annual signups pay ${Number(promoPrice) % 1 === 0 ? promoPrice : Number(promoPrice).toFixed(2)} for year one{promoEnd ? `, until ${new Date(promoEnd).toLocaleDateString()}` : ""}{promoStart ? ` (from ${new Date(promoStart).toLocaleDateString()})` : ""}. They then renew at ${Number(advAnnual) % 1 === 0 ? advAnnual : Number(advAnnual).toFixed(2)}/year.
            </div>
          )}
          <div className="flex gap-2">
            <Button onClick={savePromo} disabled={savingPromo || !(Number(promoPrice) > 0)} data-testid="save-promo-button">{savingPromo && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Save promo</Button>
            {promoStatus !== "none" && (
              <Button variant="outline" onClick={endPromo} disabled={savingPromo} data-testid="end-promo-button">End promo now</Button>
            )}
          </div>
        </div>
      )}

      {tab === "settings" && deletedShops.length > 0 && (
        <div className="max-w-lg mt-6 rounded-2xl border border-border bg-card p-6" data-testid="deleted-shops">
          <h3 className="font-heading text-lg font-bold mb-1">Deleted shops</h3>
          <p className="text-sm text-muted-foreground mb-4">Recoverable for 30 days before permanent removal.</p>
          <div className="divide-y divide-border">
            {deletedShops.map((s) => (
              <div key={s.id} className="flex items-center justify-between py-3" data-testid={`deleted-shop-${s.id}`}>
                <div><div className="font-medium">{s.name}</div><div className="text-xs text-muted-foreground">{s.region}</div></div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => restoreShop(s.id)} data-testid={`restore-shop-${s.id}`}>Restore</Button>
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => purgeShop(s.id)} data-testid={`purge-shop-${s.id}`}>Delete permanently</Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Shell>
  );
}
